import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { FiChevronLeft, FiChevronRight, FiX } from 'react-icons/fi';
import {
  archiveScope,
  listArchives,
  saveArchive,
  removeArchive,
  captureArchive,
} from './archiveStorage';
import { exportArtworkPng, downloadArtwork } from './exportArtwork';
import { scenes, sceneTitles } from './editorModel';
function SavedImage({ blob, label }) {
  const [url, setUrl] = useState('');
  useEffect(() => {
    const value = URL.createObjectURL(blob);
    setUrl(value);
    return () => URL.revokeObjectURL(value);
  }, [blob]);
  return url ? <img className="pp-edition-retained-image" src={url} alt={label} /> : null;
}
export default function EditionLibrary({
  user,
  artwork,
  data,
  locale,
  copy,
  style,
  busy,
  setBusy,
  lock,
  request,
  live,
  sameAccount,
  imageReady,
  refreshing,
  notice,
}) {
  const scope = archiveScope(window.location.origin, user.id),
    [rows, setRows] = useState([]),
    [loading, setLoading] = useState(true),
    [error, setError] = useState(''),
    [open, setOpen] = useState(false),
    [chosen, setChosen] = useState(null),
    [page, setPage] = useState(0),
    [confirm, setConfirm] = useState(null),
    [progress, setProgress] = useState('');
  const fetchRef = useRef(),
    generation = useRef(0),
    finger = useRef();
  const revision = JSON.stringify([
    data.records.dataUpdatedAt,
    data.taste.dataUpdatedAt,
    data.revision,
    locale,
    artwork.period,
  ]);
  const current = useRef(revision);
  current.current = revision;
  const load = async () => {
    const g = ++generation.current;
    fetchRef.current?.abort();
    const c = new AbortController();
    fetchRef.current = c;
    setLoading(true);
    try {
      const r = await listArchives(scope, c.signal);
      if (live.current && sameAccount() && g === generation.current) {
        setRows(r);
        setError('');
      }
    } catch {
      if (live.current && sameAccount() && !c.signal.aborted)
        setError(copy('보관한 아카이브를 읽지 못했어요.', 'Could not read saved archives.'));
    } finally {
      if (live.current && g === generation.current) setLoading(false);
    }
  };
  useEffect(() => {
    void load();
    const requests = fetchRef;
    return () => {
      requests.current?.abort();
    }; /* keyed by account in parent */ // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope]);
  const keep = async () => {
    if (
      lock.current ||
      loading ||
      error ||
      rows.length >= 5 ||
      !imageReady ||
      refreshing ||
      !sameAccount()
    )
      return;
    setConfirm(null);
    lock.current = true;
    setBusy('archive');
    setError('');
    const c = new AbortController();
    request.current = c;
    const start = current.current;
    let committed = false;
    try {
      const row = await captureArchive({
        scope,
        artwork,
        asOf: data.asOf,
        locale,
        signal: c.signal,
        isCurrent: () =>
          live.current && sameAccount() && !document.hidden && current.current === start,
        exporter: exportArtworkPng,
        onProgress: (n, total) => {
          if (live.current) setProgress(`${n} / ${total}`);
        },
        personaReason:
          artwork.period !== 'all'
            ? copy(
                '취향 유형은 전체 기간의 기록을 기준으로 분석해요. 기간을 전체로 바꿔주세요.',
                'Taste types use all-time records. Choose All time.',
              )
            : data.taste.isError
              ? copy(
                  '취향 분석을 불러오지 못했어요. 다시 시도해주세요.',
                  'Could not load your taste analysis. Please retry.',
                )
              : undefined,
      });
      if (!live.current || !sameAccount() || document.hidden || current.current !== start)
        throw Error('Snapshot changed');
      setProgress(copy('보관 마무리 중', 'Finishing storage'));
      await saveArchive(
        row,
        c.signal,
        () => live.current && sameAccount() && !document.hidden && current.current === start,
      );
      committed = true;
      if (live.current && sameAccount()) {
        setRows((v) => [row, ...v]);
        notice(
          copy(
            '아카이브 원본을 이 브라우저에 보관했어요.',
            'Archive originals saved in this browser.',
          ),
        );
      }
    } catch {
      if (live.current && sameAccount())
        notice(
          copy(
            committed
              ? '보관을 마쳤어요. 보관함을 다시 열어 확인해주세요.'
              : c.signal.aborted
                ? '아카이브 보관을 취소했어요. 기존 보관본은 유지됩니다.'
                : '아카이브 보관을 완료하지 못했어요. 기록 변경 또는 이미지·저장 오류입니다. 기존 보관본은 유지됩니다.',
            committed
              ? 'Archive saved. Reopen the library to check.'
              : c.signal.aborted
                ? 'Archiving cancelled. Existing copies are unchanged.'
                : 'Could not save archive. Records changed or image/storage preparation failed. Existing copies are unchanged.',
          ),
        );
    } finally {
      lock.current = false;
      if (live.current) {
        setBusy(false);
        setProgress('');
      }
    }
  };
  const remove = async (id) => {
    if (lock.current || !sameAccount()) return;
    setConfirm(null);
    lock.current = true;
    setBusy('archive');
    const c = new AbortController();
    request.current = c;
    try {
      await removeArchive(scope, id, c.signal);
      if (live.current && sameAccount()) {
        setRows((v) => v.filter((r) => r.id !== id));
        setChosen(null);
        setError('');
      }
    } catch {
      if (live.current && sameAccount())
        setError(
          copy('보관본 작업을 완료하지 못했어요.', 'Could not complete the archive action.'),
        );
    } finally {
      lock.current = false;
      if (live.current) setBusy(false);
    }
  };
  const turn = (d, count) => {
    if (!busy) setPage((v) => Math.min(count - 1, Math.max(0, v + d)));
  };
  return (
    <div className="pp-edition-library-tools">
      {!!artwork.edition.count && data.records.isSuccess && (
        <>
          <button
            disabled={
              busy ||
              loading ||
              !!error ||
              !imageReady ||
              refreshing ||
              data.taste.isFetching ||
              data.artistQuery.isFetching ||
              rows.length >= 5
            }
            onClick={() => setConfirm({ type: 'keep' })}
          >
            {copy(
              `전체 아카이브 원본 보관 (${rows.length}/5)`,
              `Keep this archive (${rows.length}/5)`,
            )}
          </button>
          <small>
            {copy(
              `현재 이 브라우저에 보관한 아카이브는 ${rows.length}개이며 최대 5개입니다. 브라우저 데이터 삭제·저장 공간 정리 시 유실될 수 있습니다.`,
              `${rows.length} of 5 archive slots used in this browser. Clearing site data or storage cleanup may erase these copies.`,
            )}
          </small>
        </>
      )}
      {!!progress && (
        <p role="status">
          {copy('아카이브 보관 중', 'Saving archive')} · {progress}
          <button onClick={() => request.current?.abort()}>
            {copy('보관 중단', 'Cancel archiving')}
          </button>
        </p>
      )}
      {error && (
        <p role="alert">
          {error}
          <button disabled={busy} onClick={load}>
            {copy('다시 시도', 'Retry')}
          </button>
        </p>
      )}
      {!!rows.length && (
        <button
          disabled={busy}
          onClick={() => {
            setOpen(true);
            void load();
          }}
        >
          {copy('보관함 열기', 'Open library')}
        </button>
      )}
      <Dialog
        open={open}
        onClose={() => !busy && setOpen(false)}
        maxWidth="sm"
        fullWidth
        className="pp-edition-dialog"
        PaperProps={{ className: 'pp-edition', style }}
        aria-labelledby="edition-library-title"
      >
        <DialogTitle component="div">
          <div className="pp-edition-dialog-title">
            <h2 id="edition-library-title">{copy('보관함', 'Library')}</h2>
            <button
              aria-label={copy('보관함 닫기', 'Close library')}
              disabled={busy}
              onClick={() => setOpen(false)}
            >
              <FiX size={22} />
            </button>
          </div>
        </DialogTitle>
        <DialogContent>
          <div className="pp-edition-library-content">
            <h3>{copy('보관한 아카이브 원본', 'Saved archive originals')}</h3>
            <small>
              {copy(
                '보관 당시 이미지를 그대로 다시 열고 저장합니다. 현재 리뷰·평가를 과거 상태로 덮어쓰지 않습니다.',
                'Reopen and save the original images. This never overwrites your current reviews or ratings.',
              )}
            </small>
            {loading && (
              <p role="status">{copy('보관함을 불러오고 있어요.', 'Loading library.')}</p>
            )}
            {error && (
              <p role="alert">
                {error}
                <button disabled={busy} onClick={load}>
                  {copy('다시 시도', 'Retry')}
                </button>
              </p>
            )}
            {!loading && !rows.length && (
              <p>{copy('보관한 아카이브가 없어요.', 'No saved archives.')}</p>
            )}
            {rows.map((r) => (
              <div key={r.id} className="pp-edition-saved">
                <button
                  disabled={busy}
                  aria-expanded={chosen === r.id}
                  onClick={() => {
                    setChosen(chosen === r.id ? null : r.id);
                    setPage(0);
                  }}
                >
                  {new Date(r.createdAt).toLocaleDateString(locale === 'en' ? 'en-US' : 'ko-KR')} ·{' '}
                  {r.scenes.length}
                  {copy('장면', ' scenes')}
                </button>
                {chosen === r.id && (
                  <>
                    <div
                      onTouchStart={(e) => {
                        finger.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
                      }}
                      onTouchEnd={(e) => {
                        const f = finger.current;
                        finger.current = null;
                        if (
                          f &&
                          Math.abs(e.changedTouches[0].clientY - f.y) < 18 &&
                          Math.abs(e.changedTouches[0].clientX - f.x) > 48
                        )
                          turn(e.changedTouches[0].clientX < f.x ? 1 : -1, r.images.length);
                      }}
                    >
                      <SavedImage
                        blob={r.images[page]}
                        label={copy(...sceneTitles[scenes.indexOf(r.scenes[page])])}
                      />
                    </div>
                    <div className="pp-edition-navigation">
                      <button
                        aria-label={copy('이전 보관 장면', 'Previous saved scene')}
                        disabled={busy || page === 0}
                        onClick={() => turn(-1, r.images.length)}
                      >
                        <FiChevronLeft size={22} />
                      </button>
                      <p aria-live="polite">
                        {String(page + 1).padStart(2, '0')} / 12 ·{' '}
                        {copy(...sceneTitles[scenes.indexOf(r.scenes[page])])}
                      </p>
                      <button
                        aria-label={copy('다음 보관 장면', 'Next saved scene')}
                        disabled={busy || page === 11}
                        onClick={() => turn(1, r.images.length)}
                      >
                        <FiChevronRight size={22} />
                      </button>
                    </div>
                    <button
                      className="pp-edition-primary"
                      disabled={busy}
                      onClick={() =>
                        downloadArtwork(r.images[page], `postpop-saved-${r.scenes[page]}.png`)
                      }
                    >
                      {copy('이 이미지 저장', 'Save this image')}
                    </button>
                    <button
                      disabled={busy}
                      onClick={() => setConfirm({ type: 'delete', id: r.id })}
                    >
                      {copy('이 보관본 삭제', 'Delete this saved archive')}
                    </button>
                  </>
                )}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
      <Dialog
        open={!!confirm}
        onClose={() => !busy && setConfirm(null)}
        PaperProps={{ className: 'pp-edition', style }}
        aria-labelledby="edition-storage-confirm"
      >
        <DialogTitle id="edition-storage-confirm">
          {confirm?.type === 'keep'
            ? copy(
                '아카이브 원본을 이 브라우저에 보관할까요?',
                'Keep archive originals in this browser?',
              )
            : copy('이 보관본을 삭제할까요?', 'Delete this saved archive?')}
        </DialogTitle>
        <DialogContent>
          {confirm?.type === 'keep'
            ? copy(
                '이 기간의 내 리뷰 원문·평가와 모든 개인 장면 이미지를 보관합니다. 친구 비교는 제외합니다. 서버에서 리뷰를 삭제해도 보관본은 남으며 여기서 별도로 지울 수 있습니다. 브라우저 데이터 삭제 시 유실될 수 있습니다.',
                'Stores review text, ratings and every personal scene for this period. Friend comparisons are excluded. Deleting a server review does not delete this copy; remove it here separately. Clearing browser data may erase it.',
              )
            : copy(
                '이 브라우저의 이미지와 기록 사본을 삭제합니다. 서버의 리뷰·평가는 유지됩니다. 삭제 후 복원할 수 없습니다.',
                'Removes local images and record copies, not server reviews or ratings. This cannot be undone.',
              )}
        </DialogContent>
        <DialogActions>
          <button onClick={() => setConfirm(null)} disabled={busy}>
            {copy('취소', 'Cancel')}
          </button>
          <button
            disabled={busy}
            onClick={() => (confirm.type === 'keep' ? keep() : remove(confirm.id))}
          >
            {confirm?.type === 'keep' ? copy('보관', 'Keep') : copy('삭제', 'Delete')}
          </button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
